import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Zap, LogIn, Eye, Search, CheckCircle, ArrowRight, X, ShieldCheck, Brain, Globe, Lock, RefreshCw, Code2, MessageSquareQuote, Crosshair, Users, Lightbulb } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ContactButton } from "@/components/ContactForm";
import GlobalNav from "@/components/GlobalNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [popup, setPopup] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      axios.get(`${API}/popup-config`).then(res => {
        if (res.data && res.data.enabled) {
          setPopup(res.data);
          setPopupOpen(true);
        }
      }).catch(() => {});
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/subscription`);
      setSubscription(res.data);
    } catch (err) {
      // Error fetching subscription
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="landing-page">
      {/* Announcement Popup Modal */}
      {popupOpen && popup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="announcement-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => setPopupOpen(false)} />
          <div
            className="relative w-full max-w-lg rounded-xl shadow-2xl border border-white/10 animate-fade-in z-10"
            style={{ backgroundColor: popup.bg_color || '#1a1a2e' }}
            data-testid="announcement-modal"
          >
            <button
              onClick={() => setPopupOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="announcement-close-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="p-6 sm:p-8">
              {popup.title && (
                <h2 className="text-white font-bold text-xl sm:text-2xl mb-3 pr-8" data-testid="announcement-title">
                  {popup.title}
                </h2>
              )}
              {popup.text && (
                <p className="text-zinc-300 text-sm sm:text-base leading-relaxed whitespace-pre-wrap" data-testid="announcement-text">
                  {popup.text}
                </p>
              )}
              {popup.cta_text && popup.cta_link && (
                <div className="mt-5">
                  {popup.cta_link.startsWith('http') ? (
                    <a href={popup.cta_link} target="_blank" rel="noopener noreferrer" data-testid="announcement-cta">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 px-6 rounded-sm text-sm">
                        {popup.cta_text} <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  ) : (
                    <Link to={popup.cta_link} onClick={() => setPopupOpen(false)} data-testid="announcement-cta">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 px-6 rounded-sm text-sm">
                        {popup.cta_text} <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <Helmet>
        <title>ScoreCite – Don't Let AI Stay Silent About Your Business</title>
        <meta name="description" content="ScoreCite ensures your brand is understood, trusted, and cited by AI models your customers use. Deep-dive AI Readiness audit with ready-to-deploy fixes. GEO & AI visibility optimization." />
        <meta name="keywords" content="AI visibility, AI citations, GEO optimization, generative engine optimization, AI readiness audit, website AI citability, AI search optimization, schema markup, LLM optimization, ChatGPT visibility" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.scorecite.com/" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "ScoreCite",
            "description": "AI visibility audit and GEO optimization tool. Ensures your brand is cited by AI models like ChatGPT, Claude and Gemini.",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "url": "https://www.scorecite.com/",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "1 free full CORE-level audit"
            },
            "featureList": [
              "AI Readiness audit",
              "GEO (Generative Engine Optimization)",
              "AI citation analysis",
              "Ready-to-deploy code fixes",
              "AI prompt generation",
              "Competitive analysis",
              "Market-aware diagnostics"
            ]
          }
        `}</script>
      </Helmet>

      <GlobalNav />

      {/* ═══════ Section 1: The Hero (The Hook) ═══════ */}
      <section className="relative min-h-[65vh] flex items-center justify-center px-4 pt-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 60%)' }} />
        </div>

        <div className="max-w-3xl relative z-10">
          <div className="text-center animate-fade-in">
            <Link to="/" className="hidden md:block">
              <img src="/scorecite-logo.png" alt="ScoreCite" className="h-20 mx-auto mb-6 cursor-pointer hover:opacity-90 transition-opacity" data-testid="scorecite-logo" />
            </Link>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-[2.75rem] lg:leading-tight font-bold text-white mb-6 mt-16 md:mt-0 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Don't let AI stay silent about your business.
            </h1>
            <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-4 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.2s' }}>
              Traditional SEO isn't enough anymore. ScoreCite ensures your brand is understood, trusted, and cited by the AI models your customers are using today.
            </p>
            <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-8 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.25s' }}>
              Get a deep-dive audit of your AI Readiness and a 1-minute action plan to fix it.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            {user ? (
              <Link to="/full-audit" data-testid="cta-full-audit" className="w-full sm:w-auto">
                <Button className="w-full sm:w-[220px] bg-blue-600 hover:bg-blue-500 text-white font-bold h-14 rounded-sm shadow-[0_0_20px_rgba(59,130,246,0.4)] text-base">
                  <Zap className="w-5 h-5 mr-2" /> Run Free Audit
                </Button>
              </Link>
            ) : (
              <Link to="/login" data-testid="cta-register" className="w-full sm:w-auto">
                <Button className="w-full sm:w-[220px] bg-blue-600 hover:bg-blue-500 text-white font-bold h-14 rounded-sm shadow-[0_0_20px_rgba(59,130,246,0.4)] text-base">
                  <Zap className="w-5 h-5 mr-2" /> Run Free Audit
                </Button>
              </Link>
            )}
            <Link to="/sample-report" data-testid="cta-sample-report" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-[220px] border-white/20 text-white font-bold h-14 rounded-sm text-base hover:bg-white/5">
                <Eye className="w-5 h-5 mr-2" /> View Sample Report
              </Button>
            </Link>
          </div>

          {!user && (
            <div className="text-center animate-slide-up" style={{ animationDelay: '0.4s' }}>
              <p className="text-zinc-400 text-sm">1 free full audit. No card required.</p>
            </div>
          )}
        </div>
      </section>

      {/* ═══════ Section 2: The New Reality (Problem Statement) ═══════ */}
      <section className="py-16 px-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto animate-fade-in-scroll">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-6 text-center">
            If AI doesn't understand you, it will never recommend you.
          </h2>
          <div className="p-6 sm:p-8 rounded-xl border border-white/10 bg-white/[0.02]">
            <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-4">
              Modern AI models (LLMs like ChatGPT, Claude, and Gemini) are programmed with a critical safety mechanism: <strong className="text-white">the prevention of hallucinations</strong>. If your website sends unclear signals, lacks proper structure (Schema), or misses expert-led descriptions, AI identifies you as a "high-risk" source of information.
            </p>
            <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-4">
              To avoid making mistakes, the AI will simply ignore your business. It won't cite your brand, it won't mention your services, and it will stay silent when your customers ask for recommendations.
            </p>
            <p className="text-blue-400 text-sm sm:text-base leading-relaxed font-medium">
              ScoreCite solves this. We scan your entire site to ensure you send the clear, authoritative signals that AI needs to recommend you with confidence.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ Section 3: SEO → GEO (Defining the Future) ═══════ */}
      <section className="py-16 px-4 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-10 text-center animate-fade-in-scroll">
            SEO was the beginning. GEO is the destination.
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 sm:p-8 rounded-xl border border-purple-500/20 bg-purple-500/[0.03] animate-fade-in-scroll hover:border-purple-500/40 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-white font-semibold text-lg">AI Readiness</h3>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">
                We measure how "digestible" your content is for Large Language Models. We check if your expertise, goals, and results are indexed accurately or if they are lost in technical noise.
              </p>
            </div>

            <div className="p-6 sm:p-8 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] animate-fade-in-scroll hover:border-cyan-500/40 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <MessageSquareQuote className="w-5 h-5 text-cyan-400" />
                </div>
                <h3 className="text-white font-semibold text-lg">GEO (Generative Engine Optimization)</h3>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">
                While SEO focuses on links and keywords, GEO focuses on <strong className="text-white">citations</strong>. We optimize your site so that when AI generates an answer, your brand is the one it quotes as the expert.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ Section 4: Why ScoreCite is Different ═══════ */}
      <section className="py-16 px-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto animate-fade-in-scroll">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-4 text-center">
            Deep Understanding, Local Expertise.
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base text-center mb-10 max-w-2xl mx-auto">
            We aren't just against SEO — we are SEO and much more. Most tools stop at technical errors. ScoreCite understands:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="p-5 rounded-xl border border-white/10 bg-white/[0.02] text-center group hover:border-blue-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Crosshair className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Context</h3>
              <p className="text-zinc-400 text-sm">What is your site actually about? We identify the real business identity, not what the HTML implies.</p>
            </div>
            <div className="p-5 rounded-xl border border-white/10 bg-white/[0.02] text-center group hover:border-blue-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Customer</h3>
              <p className="text-zinc-400 text-sm">Who are you trying to reach? We generate the exact questions your customers ask AI — then check if your site answers them.</p>
            </div>
            <div className="p-5 rounded-xl border border-white/10 bg-white/[0.02] text-center group hover:border-blue-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Globe className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Market</h3>
              <p className="text-zinc-400 text-sm">We understand your local market and the specific signals required to win in your niche.</p>
            </div>
          </div>
          <p className="text-zinc-300 text-sm sm:text-base text-center mt-8 max-w-2xl mx-auto">
            We don't just find problems; we provide the <strong className="text-white">exact code</strong> and <strong className="text-white">custom AI prompts</strong> you need to fix them in under a minute.
          </p>
        </div>
      </section>

      {/* ═══════ Section 5: The "No-Hostage" Guarantee ═══════ */}
      <section className="py-16 px-4 border-t border-white/5 bg-gradient-to-b from-transparent to-emerald-950/5">
        <div className="max-w-3xl mx-auto animate-fade-in-scroll">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-4 text-center">
            Your site, your code, your control. Always.
          </h2>
          <p className="text-zinc-400 text-sm text-center mb-10 max-w-xl mx-auto">
            In a market filled with applications that demand "backdoor" access to your site, ScoreCite stands for privacy and security.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="p-5 rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] hover:border-emerald-500/30 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                <h3 className="text-white font-semibold text-sm">No Integration Needed</h3>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">We never ask for sensitive data or access to your backend.</p>
            </div>
            <div className="p-5 rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] hover:border-emerald-500/30 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Lock className="w-5 h-5 text-emerald-400 shrink-0" />
                <h3 className="text-white font-semibold text-sm">Permanent Fixes</h3>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">Many tools keep your site "optimized" only while you pay. If you cancel, your site breaks. Not with us.</p>
            </div>
            <div className="p-5 rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] hover:border-emerald-500/30 transition-all duration-300">
              <div className="flex items-center gap-3 mb-3">
                <Code2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <h3 className="text-white font-semibold text-sm">Full Ownership</h3>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">The action plan and code belong to you forever. Once you apply the fixes, they stay — even if you leave us.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ Section 6: The "Why Stay?" Factor ═══════ */}
      <section className="py-16 px-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto animate-fade-in-scroll">
          <div className="p-6 sm:p-8 rounded-xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-amber-400" />
              </div>
              <h2 className="font-heading text-lg sm:text-xl font-bold text-white">
                Stay ahead in a world that never stops learning.
              </h2>
            </div>
            <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-3">
              If you get your site perfect today, why stay with ScoreCite? Because the market is dynamic. Algorithms change, and LLMs are updated constantly.
            </p>
            <p className="text-zinc-400 text-sm sm:text-base leading-relaxed">
              A site that is "AI Ready" today might be invisible tomorrow as AI models evolve their understanding. ScoreCite provides constant monitoring and real-time trends, ensuring your business stays visible no matter how the technology shifts.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ How It Works ═══════ */}
      <section className="py-16 px-4 border-t border-white/5 bg-gradient-to-b from-transparent to-blue-950/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-12 text-center animate-fade-in-scroll">
            How It Works
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl border border-white/10 bg-white/[0.02] group animate-fade-in-scroll hover:border-blue-500/30 hover:bg-white/[0.03] transition-all duration-300" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <h3 className="text-white font-semibold text-sm">Enter domain & select region</h3>
              </div>
              <p className="text-zinc-400 text-xs leading-relaxed">Choose the country and market context that matters to your business.</p>
            </div>
            <div className="p-6 rounded-xl border border-white/10 bg-white/[0.02] group animate-fade-in-scroll hover:border-blue-500/30 hover:bg-white/[0.03] transition-all duration-300" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <h3 className="text-white font-semibold text-sm">AI-powered deep analysis</h3>
              </div>
              <p className="text-zinc-400 text-xs leading-relaxed">The system crawls, analyzes and evaluates your AI visibility, trust signals, content structure and competitive position.</p>
            </div>
            <div className="p-6 rounded-xl border border-white/10 bg-white/[0.02] group animate-fade-in-scroll hover:border-blue-500/30 hover:bg-white/[0.03] transition-all duration-300" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <h3 className="text-white font-semibold text-sm">Get fixes & deploy</h3>
              </div>
              <p className="text-zinc-400 text-xs leading-relaxed">Receive ready-to-paste code, AI prompts for content, and a prioritized action plan. Fix issues in under a minute.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ Plans and Access ═══════ */}
      <section className="py-12 px-4 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-10 text-center animate-fade-in-scroll">Plans and Access</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl border border-white/10 bg-white/[0.02] animate-fade-in-scroll hover:border-white/20 transition-all duration-300" style={{ animationDelay: '0.1s' }}>
              <h3 className="text-white font-semibold mb-3">Core</h3>
              <ul className="text-zinc-400 text-sm space-y-2">
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" /><span>30 credits per month</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" /><span>1 active monitored domain</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" /><span>Historical KPI tracking</span></li>
              </ul>
            </div>
            <div className="p-6 rounded-xl border border-blue-500/30 bg-blue-500/[0.05] animate-fade-in-scroll hover:border-blue-500/50 transition-all duration-300" style={{ animationDelay: '0.2s' }}>
              <h3 className="text-white font-semibold mb-3">Pro</h3>
              <ul className="text-zinc-400 text-sm space-y-2">
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" /><span>Up to 3 active domains</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" /><span>Competitor analysis</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" /><span>Comparative advantage insights</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" /><span>Historical KPI tracking</span></li>
              </ul>
            </div>
            <div className="p-6 rounded-xl border border-purple-500/30 bg-purple-500/[0.05] animate-fade-in-scroll hover:border-purple-500/50 transition-all duration-300" style={{ animationDelay: '0.3s' }}>
              <h3 className="text-white font-semibold mb-3">Enterprise</h3>
              <ul className="text-zinc-400 text-sm space-y-2">
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400 shrink-0 mt-0.5" /><span>Custom domains and credits</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400 shrink-0 mt-0.5" /><span>Custom integrations</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-400 shrink-0 mt-0.5" /><span>Historical KPI tracking</span></li>
              </ul>
            </div>
          </div>
          <div className="flex justify-center mt-8 animate-fade-in-scroll">
            <Link to="/pricing">
              <Button variant="outline" className="border-white/20 text-white font-medium h-10 rounded-sm text-sm hover:bg-white/5 hover:border-white/30 transition-all duration-300">
                See pricing details <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ Final CTA ═══════ */}
      <section className="py-16 px-4 border-t border-white/5 bg-gradient-to-b from-transparent to-blue-950/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-6 animate-fade-in-scroll">
            Don't wait for AI to notice you.<br />
            Make it impossible to ignore you.
          </h2>
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-8 animate-fade-in-scroll">
            Run your first audit and see exactly how AI sees your business — and what to fix to be cited.
          </p>
          <div className="flex justify-center animate-fade-in-scroll">
            {user ? (
              <Link to="/full-audit" className="w-full sm:w-auto">
                <Button className="w-full sm:w-[220px] bg-blue-600 hover:bg-blue-500 text-white font-bold h-14 rounded-sm shadow-[0_0_20px_rgba(59,130,246,0.4)] text-base">
                  <Zap className="w-5 h-5 mr-2" /> Run Free Audit
                </Button>
              </Link>
            ) : (
              <Link to="/login" className="w-full sm:w-auto">
                <Button className="w-full sm:w-[220px] bg-blue-600 hover:bg-blue-500 text-white font-bold h-14 rounded-sm shadow-[0_0_20px_rgba(59,130,246,0.4)] text-base">
                  <Zap className="w-5 h-5 mr-2" /> Run Free Audit
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-zinc-600 text-xs">&copy; 2025 ScoreCite. AI visibility audit and GEO optimization tool.</p>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link to="/about" className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors">About</Link>
            <Link to="/faq" className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors">FAQ</Link>
            <Link to="/pricing" className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors">Pricing</Link>
            <ContactButton className="text-xs" />
            <Link to="/terms" className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
